/**
 * WelcomePackImageReader
 * ────────────────────────────────────────────────────────────────────────
 * Renders the HiBFF Welcome Pack charters as images (one JPEG per page)
 * inside the homepage modal.
 *
 * Why this replaced the previous PDF.js / <iframe> / <object> attempts:
 *   • PDFs behind Cloudflare's edge are served with octet-stream + nosniff,
 *     so native browsers refuse to inline them.
 *   • PDF.js (react-pdf) requires a webworker which Create React App's
 *     production webpack build does not reliably bundle as a static asset.
 *   • Plain <img> tags Just Work — every browser, every webview, every
 *     CDN, no MIME games, no worker URLs, no failure modes.
 *
 * The original .pdf files are still served from /api/static/charters/ and
 * are reachable via the modal's "Open in new tab" / "Download" buttons.
 *
 * Props:
 *   images : string[]   Ordered list of image URLs (one per page).
 *   label  : string     Human label used in alt text & paging copy.
 */
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WelcomePackImageReader = ({ images, label }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const total = images.length;

  // Reset to page 1 whenever the document switches (parent ↔ teen) so the
  // user doesn't land on, e.g., page 5 of an 8-page doc when reopening.
  useEffect(() => {
    setPageIndex(0);
  }, [images]);

  return (
    <div
      className="bg-gray-100 flex flex-col"
      style={{ minHeight: '60vh' }}
      data-testid="welcome-pack-image-reader"
    >
      <div className="flex-1 overflow-y-auto py-4 px-3 flex flex-col items-center">
        <img
          src={images[Math.min(pageIndex, total - 1)]}
          alt={`${label} — page ${pageIndex + 1} of ${total}`}
          className="shadow-md w-full h-auto bg-white"
          style={{ maxWidth: '720px' }}
          loading="eager"
          data-testid="welcome-pack-image"
        />
      </div>

      {total > 1 && (
        <div
          className="border-t border-gray-200 bg-white px-4 py-2 flex items-center justify-center gap-3"
          data-testid="welcome-pack-image-pager"
        >
          <button
            type="button"
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            disabled={pageIndex <= 0}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
            data-testid="welcome-pack-image-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-gray-600">
            Page <strong>{pageIndex + 1}</strong> of {total}
          </span>
          <button
            type="button"
            onClick={() => setPageIndex((p) => Math.min(total - 1, p + 1))}
            disabled={pageIndex >= total - 1}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
            data-testid="welcome-pack-image-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default WelcomePackImageReader;
